import { useAction } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import { useState } from 'react'
import { Controller } from 'react-hook-form'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { isPasswordValid } from '@root/entities/auth/constants'
import { backClickedAction } from '@root/features/initialization/atoms/common'
import {
  policyScreenAction,
  type SettingScreenPolicy,
} from '@root/features/settings/settings-screen'
import { useTranslation } from '@root/shared/localization'
import {
  AuthButton,
  AuthInput,
  AuthModalHeading,
} from '@root/shared/ui/auth-modal'
import { Checkbox, InputPassword } from '@root/shared/ui-kit'

import { MIN_USERNAME_LENGTH, type AuthFormValues } from './auth-register'

import type { Intent } from '@root/shared/ui-kit'
import type { Control } from 'react-hook-form'

type AuthRegisterMainProps = {
  control: Control<AuthFormValues>
  formError?: string
  checkIsUsernameAvailableState: AsyncStateN.AsyncStateN<unknown, unknown>
  // TODO: Ivan - use for button busy/disabled state
  registerRequestState: AsyncStateN.AsyncStateN<unknown, unknown>
  usernameIntent: Intent
}

export const AuthRegisterMain: React.FC<AuthRegisterMainProps> = ({
  control,
  formError,
  checkIsUsernameAvailableState,
  usernameIntent,
  registerRequestState,
}) => {
  const [tosAccepted, setTosAccepted] = useState(false)

  const { t } = useTranslation()
  const backClicked = useAction(backClickedAction)
  const policyScreen = useAction(policyScreenAction)

  const handlePolicyClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    policy: SettingScreenPolicy,
  ) => {
    e.preventDefault()
    policyScreen(policy)
  }

  return (
    <Root>
      <AuthModalHeading
        title={t('auth.registration')}
        onBack={() => backClicked()}
        mobileInfoMessage={t('auth.info.wallets')}
      />
      <Group>
        <Controller
          rules={{
            required: true,
            minLength: {
              value: MIN_USERNAME_LENGTH,
              message: t('auth.username-length-error'),
            },
          }}
          control={control}
          name="username"
          render={({ field }) => {
            return (
              <AuthInput
                {...field}
                intent={usernameIntent}
                autoFocus
                startContent={<StartContent>{t('auth.username')}</StartContent>}
                autoCorrect="off"
                spellCheck="false"
                autoComplete="username"
                size="m"
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replaceAll(
                    /[^\w.-]/g,
                    '',
                  )
                }}
              />
            )
          }}
        />
        <Controller
          rules={{ required: true, validate: isPasswordValid }}
          control={control}
          name="password"
          render={({ field }) => (
            <InputPasswordAlt
              {...field}
              startContent={t('auth.password')}
              autoCorrect="off"
              spellCheck="false"
              autoComplete="password"
              size="m"
            />
          )}
        />
        <Controller
          rules={{
            required: true,
            validate: (rpt, { password }) => {
              if (rpt !== password) {
                return t('settings.password.toast-repeat-error')
              }
            },
          }}
          control={control}
          name="repeatPassword"
          render={({ field }) => (
            <InputPasswordAlt
              {...field}
              startContent={t('auth.repeat')}
              autoCorrect="off"
              spellCheck="false"
              autoComplete="new-password"
              size="m"
            />
          )}
        />
        <AnimatePresence>
          {formError ? <ErrorMessage>{formError}</ErrorMessage> : undefined}
        </AnimatePresence>
      </Group>
      <Group>
        <Hint>{t('auth.register.enter-referral-code')}</Hint>
        <Controller
          control={control}
          name="referralCode"
          render={({ field }) => (
            <AuthInput
              {...field}
              autoCorrect="off"
              spellCheck="false"
              size="m"
            />
          )}
        />
      </Group>
      <AuthRegisterTerms>
        <Checkbox
          id="tos-accepted"
          checked={tosAccepted}
          onCheckedChange={(e: boolean | 'indeterminate') => {
            if (typeof e === 'boolean') {
              setTosAccepted(e)
            }
          }}
        />
        <TermsOfService htmlFor="tos-accepted">
          <Trans
            i18nKey="auth.register.terms-of-service"
            components={[
              <a
                key={0}
                onClick={(event) =>
                  handlePolicyClick(event, 'terms-of-service')
                }
              />,
              <a
                key={1}
                onClick={(event) => handlePolicyClick(event, 'privacy-policy')}
              />,
            ]}
          />
        </TermsOfService>
      </AuthRegisterTerms>
      <RegisterButton
        disabled={!!formError || !tosAccepted}
        busy={
          AsyncStateN.isLoading(checkIsUsernameAvailableState) ||
          AsyncStateN.isLoading(registerRequestState)
        }
      >
        {t('auth.continue')}
      </RegisterButton>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 16px;
`

const Group = styled.div`
  display: grid;
  gap: 12px;
`

const Hint = styled.h3`
  color: var(--textA-12);
  text-align: center;

  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`

const StartContent = styled.div`
  width: 70px;
`

const ErrorMessage = styled.div`
  text-align: center;
  color: var(--red-9);
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`

const RegisterButton = styled(AuthButton)``

const AuthRegisterTerms = styled.div`
  display: grid;
  gap: 8px;

  grid-template-columns: auto 1fr;
`

const TermsOfService = styled.label`
  color: var(--textA-9);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  a {
    color: var(--textA-9);
    text-shadow: 0px 0px 0px var(--background-8);

    transition: color 0.15s linear;

    &:hover {
      color: var(--text-12);
    }
  }
`

const InputPasswordAlt = styled(InputPassword)`
  --input-background: var(--appearanceA-2);

  &:focus-within {
    --input-background-active: var(--appearanceA-5);
  }
`
